import React, { useMemo } from 'react';
import _ from 'lodash';

import { useAITTheme } from '@this/src/components/shared/ui/theme';
import type TransportElement from '@this/domain/transport_element2';
import type OrderItem from '@this/domain/order_item2';
import {
  convertNumberToShinkansenSeatType,
  DEFAULT_SHINKANSEN_SEAT_TYPE
} from '@this/src/domain/traveler/shinkansen_type_conversion';
import { initializeTravelerShinkansenTypes } from './railway_ticket_type_form';

export type ValueType = 'singleTraveler' | 'multipleTravelers' | 'multipleTravelersWithSeatType';

interface Props {
  element: TransportElement;
  orderItem: OrderItem;
  transportIndex: number;
  classNameForModifiedField: (
    pathName: 'shinkansenSeatType' | 'shinkansenTicketType',
    transportIndex: number,
    travelerIndex?: number
  ) => string;
}

export const RailwaySeatTypeForm: React.FC<Props> = ({
  element,
  orderItem,
  transportIndex,
  classNameForModifiedField
}) => {
  const { theme } = useAITTheme();

  const perTransport = orderItem.supportsPerTransportShinkansenTypes();
  const valueType = useMemo((): ValueType => {
    return convertValueType(element.railwaySeatType, perTransport);
  }, [element.railwaySeatType, perTransport]);

  if (valueType === 'singleTraveler') {
    return (
      <select
        value={element.railwaySeatType || 0}
        onChange={e => {
          orderItem.handleChangeRailwaySeatType(element, parseInt(e.target.value, 10));
          app.render();
        }}
      >
        <option value={0}>自由席</option>
        <option value={1}>指定席</option>
        <option value={2}>グリーン車</option>
      </select>
    );
  }

  return (
    <>
      {orderItem.travelerInformations.map((ti, travelerIndex) => (
        <div
          key={travelerIndex}
          className={classNameForModifiedField('shinkansenSeatType', transportIndex, travelerIndex)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: theme.tokens.colors.text.placeholder
          }}
        >
          <select
            value={travelerShinkansenSeatType(valueType, orderItem, transportIndex, travelerIndex) || 0}
            onChange={e =>
              setShinkansenSeatTypeFromNumber(
                valueType,
                orderItem,
                transportIndex,
                travelerIndex,
                parseInt(e.target.value, 10)
              )
            }
          >
            <option value={0}>自由席</option>
            <option value={1}>指定席</option>
            <option value={2}>グリーン車</option>
          </select>
          {(travelerIndex > 0 || orderItem.travelerInformations.length > 1) && (
            <span>({ti.lastNameKana ? `${ti.lastNameKana} ${ti.firstNameKana}` : ti.user?.email})</span>
          )}
        </div>
      ))}
    </>
  );
};

/**
 * 本体（element）に値があれば一括モード。
 * 0（自由席・通常きっぷ）も値ありとして扱い、本体値を優先する backend の present? 判定と対称にする
 */
export const convertValueType = (value: number | null | undefined, perTransport: boolean): ValueType => {
  if (value != null) {
    return 'singleTraveler';
  }

  if (perTransport) {
    return 'multipleTravelersWithSeatType';
  }

  return 'multipleTravelers';
};

const travelerShinkansenSeatType = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number
) => {
  if (orderItem.travelerInformations.length === 0) {
    return null;
  }

  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];
  const seatType =
    valueType === 'multipleTravelersWithSeatType'
      ? traveler.travelerInformationShinkansenTypes?.[transportIndex]?.shinkansenSeatType ??
        traveler.shinkansenSeatType
      : traveler.shinkansenSeatType;

  switch (seatType) {
    case 'reserve':
      return 1;
    case 'green':
      return 2;
    default:
      return 0;
  }
};

const setShinkansenSeatTypeFromNumber = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number,
  value: number
) => {
  if (valueType === 'multipleTravelersWithSeatType') {
    initializeTravelerShinkansenTypes(orderItem);
  }

  const shinkansenSeatType = convertNumberToShinkansenSeatType(value) ?? DEFAULT_SHINKANSEN_SEAT_TYPE;
  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];

  if (valueType === 'multipleTravelersWithSeatType') {
    const shinkansenType = traveler.travelerInformationShinkansenTypes?.[transportIndex];

    if (shinkansenType) {
      shinkansenType.shinkansenSeatType = shinkansenSeatType;
    }
  } else {
    traveler.shinkansenSeatType = shinkansenSeatType;
  }

  app.render();
};
